import { EMAIL_REGEX, sanitizeEmailInput, sanitizeInput } from '../../helpers/sanitizeInput.helper'
import { isValidHexColor } from './b2bAccount.helper'
import {
  CONTACT_LEAD_CONTACT_NAME_MAX_LENGTH,
  CONTACT_LEAD_MESSAGE_MAX_LENGTH,
  CONTACT_LEAD_ORG_NAME_MAX_LENGTH,
  ORG_CONTACT_EMAIL_MAX_LENGTH,
  ORG_CONTACT_PHONE_MAX_LENGTH,
  ORG_DESCRIPTION_MAX_LENGTH,
  ORG_DISPLAY_NAME_MAX_LENGTH,
  ORG_NAME_MAX_LENGTH,
  ORG_NAME_MIN_LENGTH,
  ORG_WEBSITE_URL_MAX_LENGTH,
} from '../constants/orgForm.constants'
import { normalizeOrgContactPhone } from './orgPhoneValidation.helper'
import type { B2bAccountCreatePayload } from '../types/b2bAccount.types'

const HEX_COLOR_6_REGEX = /^#[0-9A-Fa-f]{6}$/

export interface OrgCreateFormInput {
  name: string
  displayName: string
  description: string
  contactEmail: string
  contactPhone: string
  websiteUrl: string
  primaryColor: string
  secondaryColor: string
}

export interface OrgContactLeadFormInput {
  orgName: string
  contactName: string
  contactEmail: string
  message: string
}

export interface ValidationResult<T> {
  ok: boolean
  value?: T
  error?: string
}

function cleanOptionalText(input: string, maxLength: number): string {
  const withoutScriptTags = input.replace(/<\/?script[^>]*>/gi, '')
  const sanitized = sanitizeInput(withoutScriptTags, true).trim()
  return sanitized.slice(0, maxLength)
}

function cleanOptionalEmail(input: string): string {
  return sanitizeEmailInput(input).slice(0, ORG_CONTACT_EMAIL_MAX_LENGTH)
}

function cleanOptionalHexColor(input: string): string {
  const sanitized = input.replace(/[^#0-9a-fA-F]/g, '').toUpperCase()
  return sanitized.slice(0, 7)
}

function cleanOptionalContactPhone(input: string): string {
  const trimmed = input.trim()
  const withoutSeparators = trimmed.replace(/[\s\-().]/g, '')
  return withoutSeparators.slice(0, ORG_CONTACT_PHONE_MAX_LENGTH + 4)
}

function cleanOptionalWebsiteUrl(input: string): string {
  const normalized = input.trim()
  const noControlChars = normalized
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/[\n\r]/g, '')
  const withProtocol =
    noControlChars && !noControlChars.match(/^https?:\/\//i) ? `https://${noControlChars}` : noControlChars
  return withProtocol.slice(0, ORG_WEBSITE_URL_MAX_LENGTH)
}

export function sanitizeOrgCreateFormInput(input: OrgCreateFormInput): OrgCreateFormInput {
  return {
    name: cleanOptionalText(input.name, ORG_NAME_MAX_LENGTH),
    displayName: cleanOptionalText(input.displayName, ORG_DISPLAY_NAME_MAX_LENGTH),
    description: cleanOptionalText(input.description, ORG_DESCRIPTION_MAX_LENGTH),
    contactEmail: cleanOptionalEmail(input.contactEmail),
    contactPhone: cleanOptionalContactPhone(input.contactPhone),
    websiteUrl: cleanOptionalWebsiteUrl(input.websiteUrl),
    primaryColor: cleanOptionalHexColor(input.primaryColor),
    secondaryColor: cleanOptionalHexColor(input.secondaryColor),
  }
}

export function validateOrgCreatePayload(
  input: OrgCreateFormInput,
): ValidationResult<B2bAccountCreatePayload> {
  const clean = sanitizeOrgCreateFormInput(input)
  if (clean.name.length < ORG_NAME_MIN_LENGTH || clean.name.length > ORG_NAME_MAX_LENGTH) {
    return {
      ok: false,
      error: `Organization name must be ${ORG_NAME_MIN_LENGTH}-${ORG_NAME_MAX_LENGTH} characters`,
    }
  }
  if (clean.displayName.length > 0 && clean.displayName.length > ORG_DISPLAY_NAME_MAX_LENGTH) {
    return { ok: false, error: `Display name must be ${ORG_DISPLAY_NAME_MAX_LENGTH} characters or fewer` }
  }
  if (clean.description.length > ORG_DESCRIPTION_MAX_LENGTH) {
    return { ok: false, error: `Description must be ${ORG_DESCRIPTION_MAX_LENGTH} characters or fewer` }
  }
  if (!clean.contactEmail) {
    return { ok: false, error: 'Contact email is required' }
  }
  if (!EMAIL_REGEX.test(clean.contactEmail)) {
    return { ok: false, error: 'Contact email must be a valid email address' }
  }
  const normalizedPhone = clean.contactPhone.trim()
    ? normalizeOrgContactPhone(clean.contactPhone)
    : null
  if (clean.contactPhone.trim() && !normalizedPhone) {
    return {
      ok: false,
      error: 'Phone number must be a valid E.164 format (e.g. +14155552671)',
    }
  }
  if (clean.websiteUrl.length > ORG_WEBSITE_URL_MAX_LENGTH) {
    return { ok: false, error: `Website URL must be ${ORG_WEBSITE_URL_MAX_LENGTH} characters or fewer` }
  }
  if (
    clean.primaryColor.length > 0 &&
    (!isValidHexColor(clean.primaryColor) || !HEX_COLOR_6_REGEX.test(clean.primaryColor))
  ) {
    return { ok: false, error: 'Primary color must use #RRGGBB format' }
  }
  if (
    clean.secondaryColor.length > 0 &&
    (!isValidHexColor(clean.secondaryColor) || !HEX_COLOR_6_REGEX.test(clean.secondaryColor))
  ) {
    return { ok: false, error: 'Secondary color must use #RRGGBB format' }
  }

  const payload: B2bAccountCreatePayload = { name: clean.name }
  if (normalizedPhone) payload.contactPhone = normalizedPhone
  if (clean.displayName) payload.displayName = clean.displayName
  if (clean.description) payload.description = clean.description
  if (clean.contactEmail) payload.contactEmail = clean.contactEmail
  if (clean.websiteUrl) payload.websiteUrl = clean.websiteUrl
  if (clean.primaryColor) payload.primaryColor = clean.primaryColor
  if (clean.secondaryColor) payload.secondaryColor = clean.secondaryColor
  return { ok: true, value: payload }
}

export function sanitizeContactLeadFormInput(input: OrgContactLeadFormInput): OrgContactLeadFormInput {
  return {
    orgName: cleanOptionalText(input.orgName, CONTACT_LEAD_ORG_NAME_MAX_LENGTH),
    contactName: cleanOptionalText(input.contactName, CONTACT_LEAD_CONTACT_NAME_MAX_LENGTH),
    contactEmail: cleanOptionalEmail(input.contactEmail),
    message: cleanOptionalText(input.message, CONTACT_LEAD_MESSAGE_MAX_LENGTH),
  }
}

export function validateContactLeadPayload(
  input: OrgContactLeadFormInput,
): ValidationResult<OrgContactLeadFormInput> {
  const clean = sanitizeContactLeadFormInput(input)
  if (clean.orgName.length < ORG_NAME_MIN_LENGTH || clean.orgName.length > CONTACT_LEAD_ORG_NAME_MAX_LENGTH) {
    return {
      ok: false,
      error: `Organization name must be ${ORG_NAME_MIN_LENGTH}-${CONTACT_LEAD_ORG_NAME_MAX_LENGTH} characters`,
    }
  }
  if (
    clean.contactName.length < ORG_NAME_MIN_LENGTH ||
    clean.contactName.length > CONTACT_LEAD_CONTACT_NAME_MAX_LENGTH
  ) {
    return {
      ok: false,
      error: `Contact name must be ${ORG_NAME_MIN_LENGTH}-${CONTACT_LEAD_CONTACT_NAME_MAX_LENGTH} characters`,
    }
  }
  if (!EMAIL_REGEX.test(clean.contactEmail)) {
    return { ok: false, error: 'Email must be a valid email address' }
  }
  if (clean.message.length > CONTACT_LEAD_MESSAGE_MAX_LENGTH) {
    return { ok: false, error: `Message must be ${CONTACT_LEAD_MESSAGE_MAX_LENGTH} characters or fewer` }
  }
  return { ok: true, value: clean }
}

